/**
 * Gas Consumption Charts Component
 * Displays various charts for gas consumption analytics
 */

import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { analyticsService, GasAnalytics } from '../services/analytics.service';

interface GasConsumptionChartsProps {
  filters?: {
    startDate?: string;
    endDate?: string;
    operationType?: string;
  };
  selectedCurrency: 'HBAR' | 'USD' | 'INR';
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const OPERATION_COLORS: Record<string, string> = {
  'PRODUCT_ACTIVATION': '#0088FE',
  'QR_SCAN': '#00C49F',
  'CONSUMPTION_MARKING': '#FFBB28',
  'TRANSFER': '#FF8042',
  'BATCH_REGISTRATION': '#8884D8'
};

export const GasConsumptionCharts: React.FC<GasConsumptionChartsProps> = ({
  filters,
  selectedCurrency
}) => {
  const [gasAnalytics, setGasAnalytics] = useState<GasAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGasAnalytics();
  }, [filters]);

  const fetchGasAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await analyticsService.getGasAnalytics(filters);
      setGasAnalytics(data);
    } catch (err) {
      setError('Failed to fetch gas analytics data');
      console.error('Error fetching gas analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCostValue = (item: any, currency: 'HBAR' | 'USD' | 'INR'): number => {
    switch (currency) {
      case 'HBAR':
        return item.totalCostHBAR || item.hbar || 0;
      case 'USD':
        return item.totalCostUSD || item.usd || 0;
      case 'INR':
        return item.totalCostINR || item.inr || 0;
      default:
        return 0;
    }
  };

  const formatCurrency = (value: number, currency: 'HBAR' | 'USD' | 'INR') => {
    switch (currency) {
      case 'HBAR':
        return `${value.toFixed(8)} HBAR`;
      case 'USD':
        return `$${value.toFixed(4)}`;
      case 'INR':
        return `₹${value.toFixed(2)}`;
      default:
        return value.toString();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading gas analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!gasAnalytics) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No gas analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cost Trends Chart */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Gas Cost Trends ({selectedCurrency})
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={gasAnalytics.costTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
              tickFormatter={(value: any) => new Date(value).toLocaleDateString()}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={(value: any) => formatCurrency(value, selectedCurrency)}
            />
            <Tooltip 
              formatter={(value: any) => value !== undefined ? formatCurrency(value, selectedCurrency) : ''}
              labelFormatter={(label: any) => `Date: ${new Date(label).toLocaleDateString()}`}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey={(item: any) => getCostValue(item, selectedCurrency)}
              stroke="#0088FE"
              strokeWidth={2}
              dot={{ r: 4 }}
              name={`Cost (${selectedCurrency})`}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Operation Breakdown Bar Chart */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Cost by Operation Type ({selectedCurrency})
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={gasAnalytics.operationBreakdown}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="operationType" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={(value: any) => formatCurrency(value, selectedCurrency)}
            />
            <Tooltip 
              formatter={(value: any) => value !== undefined ? formatCurrency(value, selectedCurrency) : ''}
            />
            <Legend />
            <Bar
              dataKey={(item: any) => getCostValue(item, selectedCurrency)}
              fill="#0088FE"
              name={`Total Cost (${selectedCurrency})`}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Operation Distribution Pie Chart */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Transaction Distribution by Operation Type
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={gasAnalytics.operationBreakdown}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ operationType, transactionCount, percent }: any) => 
                `${operationType}: ${transactionCount} (${(percent * 100).toFixed(1)}%)`
              }
              outerRadius={80}
              fill="#8884d8"
              dataKey="transactionCount"
            >
              {gasAnalytics.operationBreakdown.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={OPERATION_COLORS[entry.operationType] || COLORS[index % COLORS.length]} 
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: any) => value !== undefined ? value : 0}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Average Cost Comparison */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Average Cost per Operation ({selectedCurrency})
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart 
            data={gasAnalytics.operationBreakdown}
            layout="horizontal"
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number"
              tick={{ fontSize: 12 }}
              tickFormatter={(value: any) => formatCurrency(value, selectedCurrency)}
            />
            <YAxis 
              type="category"
              dataKey="operationType" 
              tick={{ fontSize: 12 }}
              width={120}
            />
            <Tooltip 
              formatter={(value: any) => value !== undefined ? formatCurrency(value, selectedCurrency) : ''}
            />
            <Legend />
            <Bar
              dataKey={`averageCost${selectedCurrency}`}
              fill="#00C49F"
              name={`Average Cost (${selectedCurrency})`}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Statistics */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Summary Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-blue-600">Total Gas Used</div>
            <div className="text-2xl font-bold text-blue-900">
              {gasAnalytics.totalGasUsed.toLocaleString()}
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-green-600">
              Total Cost ({selectedCurrency})
            </div>
            <div className="text-2xl font-bold text-green-900">
              {formatCurrency(getCostValue(gasAnalytics, selectedCurrency), selectedCurrency)}
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-purple-600">
              Average Cost per Transaction ({selectedCurrency})
            </div>
            <div className="text-2xl font-bold text-purple-900">
              {formatCurrency(getCostValue(gasAnalytics.averageCostPerOperation, selectedCurrency), selectedCurrency)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GasConsumptionCharts;